import { QRCode } from '@/components/qr/QRCode';
import { getOrdinal } from '@/lib/utils';
import {
  Button,
  FormFieldGroup,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
  Textarea,
} from '@startupquickstart/frontend';
import {
  DEFAULT_ACTIVATION_RADIUS_M,
  type ActivationMode,
  type ChallengeEditorSectionProps,
} from '../types';
import { ACTIVATION_RADIUS_MAX_M, ACTIVATION_RADIUS_MIN_M, IBEACON_VALUE_MAX } from '../validation';
import { SectionCard } from './SectionCard';

const ACTIVATION_MODE_OPTIONS: ReadonlyArray<{ value: ActivationMode; label: string }> = [
  { value: 'available', label: 'Available (default — playable as soon as the group unlocks)' },
  { value: 'manual', label: 'Manual — team taps "Start" before the clue is revealed' },
  { value: 'gps_proximity', label: 'GPS proximity — team must be within the radius' },
  { value: 'qr_code', label: 'QR scan — team scans the printed QR to start' },
  { value: 'ibeacon', label: "iBeacon — team's mobile detects the configured beacon" },
];

/** Parse a positive-int input; empty/invalid → null. */
function parsePositiveInt(text: string): number | null {
  const parsed = parseInt(text, 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

/**
 * Event-only gameplay rules: activation gate (incl. GPS radius, activation
 * QR, iBeacon triple — restored from the retired edit dialog), facilitator
 * supervision, max completions, and speed bonuses.
 */
export function GameplaySection({ values, errors, updateField }: ChallengeEditorSectionProps) {
  const isRoadblock = values.type === 'roadblock';

  return (
    <SectionCard
      id="gameplay"
      title="Gameplay"
      description="How this challenge activates and scores during the event"
    >
      {/* Activation gate (DT-157/186/187/188) */}
      <FormFieldGroup
        name="activationMode"
        label="Activation"
        description="Layered on top of group unlocks — the group still has to be unlocked before the activation gate applies."
      >
        <Select
          value={values.activationMode}
          onValueChange={v => updateField('activationMode', v as ActivationMode)}
        >
          <SelectTrigger id="activationMode" className="w-full">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {ACTIVATION_MODE_OPTIONS.map(option => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </FormFieldGroup>

      {values.activationMode === 'gps_proximity' && (
        <FormFieldGroup
          name="activationRadius"
          label="Activation radius (meters)"
          description={`Players within this distance of the pinned location can activate the challenge (${ACTIVATION_RADIUS_MIN_M}–${ACTIVATION_RADIUS_MAX_M} m). Set the challenge's location too, otherwise activation will refuse.`}
          error={errors.activationRadius}
        >
          <Input
            id="activationRadius"
            type="number"
            min={ACTIVATION_RADIUS_MIN_M}
            max={ACTIVATION_RADIUS_MAX_M}
            value={values.activationRadius ?? ''}
            onChange={e => updateField('activationRadius', parsePositiveInt(e.target.value))}
            placeholder={String(DEFAULT_ACTIVATION_RADIUS_M)}
          />
        </FormFieldGroup>
      )}

      {/* DT-187. Activation QR — the qrCodeId is generated server-side when
          the challenge is saved with mode=qr_code. */}
      {values.activationMode === 'qr_code' && (
        <div className="space-y-2 rounded-md border border-border bg-muted/20 p-4 sm:flex sm:items-start sm:gap-4 sm:space-y-0">
          {values.qrCodeId ? (
            <>
              <QRCode
                value={values.qrCodeId}
                size={140}
                alt={`Activation QR for ${values.name || 'challenge'}`}
              />
              <div className="flex-1 space-y-1">
                <span className="font-mono text-xs uppercase tracking-wider text-muted-foreground">
                  Activation QR
                </span>
                <p className="text-sm">
                  Print this QR and place it at the challenge location. Teams scan it on mobile to
                  start.
                </p>
                <p className="font-mono text-xs text-muted-foreground">
                  ID: <code>{values.qrCodeId}</code>
                </p>
              </div>
            </>
          ) : (
            <p className="text-sm text-muted-foreground">
              Save the challenge to generate the activation QR.
            </p>
          )}
        </div>
      )}

      {/* DT-188. iBeacon triple. Mobile detection needs the native Bluetooth
          scanner; facilitators can force-activate until it ships. */}
      {values.activationMode === 'ibeacon' && (
        <div className="space-y-3 rounded-md border border-border bg-muted/20 p-4">
          <p className="text-xs text-muted-foreground">
            Configure the beacon advertised at this challenge's location. Mobile detects the
            matching triple and activates automatically when in range. Until the native scanner
            ships, facilitators can still force-activate from the team detail page.
          </p>
          <FormFieldGroup name="ibeaconUuid" label="UUID" error={errors.ibeaconUuid}>
            <Input
              id="ibeaconUuid"
              value={values.ibeaconUuid}
              onChange={e => updateField('ibeaconUuid', e.target.value)}
              placeholder="fda50693-a4e2-4fb1-afcf-c6eb07647825"
              maxLength={64}
            />
          </FormFieldGroup>
          <div className="grid grid-cols-2 gap-3">
            <FormFieldGroup
              name="ibeaconMajor"
              label={`Major (0–${IBEACON_VALUE_MAX})`}
              error={errors.ibeaconMajor}
            >
              <Input
                id="ibeaconMajor"
                type="number"
                min={0}
                max={IBEACON_VALUE_MAX}
                value={values.ibeaconMajor ?? ''}
                onChange={e => {
                  const parsed = parseInt(e.target.value, 10);
                  updateField('ibeaconMajor', Number.isFinite(parsed) ? parsed : null);
                }}
              />
            </FormFieldGroup>
            <FormFieldGroup
              name="ibeaconMinor"
              label={`Minor (0–${IBEACON_VALUE_MAX})`}
              error={errors.ibeaconMinor}
            >
              <Input
                id="ibeaconMinor"
                type="number"
                min={0}
                max={IBEACON_VALUE_MAX}
                value={values.ibeaconMinor ?? ''}
                onChange={e => {
                  const parsed = parseInt(e.target.value, 10);
                  updateField('ibeaconMinor', Number.isFinite(parsed) ? parsed : null);
                }}
              />
            </FormFieldGroup>
          </div>
        </div>
      )}

      {/* Facilitator supervision (DT-164) */}
      <div className="flex items-start justify-between gap-6 rounded-lg border bg-muted/20 p-4">
        <div>
          <p className="text-sm font-medium text-foreground">Requires Facilitator</p>
          <p className="text-sm text-muted-foreground">
            {isRoadblock
              ? 'Always on for Roadblock challenges. Staff complete the loop via the facilitator endpoint.'
              : 'When on, mobile hides the submission form and shows the instructions below.'}
          </p>
        </div>
        <Switch
          checked={values.requiresFacilitator || isRoadblock}
          disabled={isRoadblock}
          onCheckedChange={checked => updateField('requiresFacilitator', checked)}
        />
      </div>

      <FormFieldGroup
        name="facilitatorInstructions"
        label="Facilitator Instructions"
        description="Shown to the team on mobile + to staff on the facilitator dashboard. Plain text."
      >
        <Textarea
          id="facilitatorInstructions"
          value={values.facilitatorInstructions}
          onChange={e => updateField('facilitatorInstructions', e.target.value)}
          placeholder="e.g., Find the staff at the registration desk. Have all 4 teammates do a group jump for the camera."
          rows={4}
          maxLength={2000}
        />
      </FormFieldGroup>

      <FormFieldGroup
        name="maxCompletions"
        label="Max Completing Teams"
        description="Challenge disappears after this many teams complete it. Leave empty for unlimited."
        error={errors.maxCompletions}
      >
        <Input
          id="maxCompletions"
          type="number"
          min={1}
          value={values.maxCompletions ?? ''}
          onChange={e => updateField('maxCompletions', parsePositiveInt(e.target.value))}
          placeholder="Unlimited"
        />
      </FormFieldGroup>

      <FormFieldGroup
        name="maxAttempts"
        label="Attempts Allowed"
        description="Wrong answers a team may give before the challenge closes, reveals the answer and scores nothing. Leave empty to use the event default; enter 0 for unlimited."
        error={errors.maxAttempts}
      >
        <Input
          id="maxAttempts"
          type="number"
          min={0}
          value={values.maxAttempts ?? ''}
          onChange={e =>
            updateField(
              'maxAttempts',
              e.target.value === '' ? null : Math.max(0, Number.parseInt(e.target.value, 10) || 0),
            )
          }
          placeholder="Event default"
        />
      </FormFieldGroup>

      <FormFieldGroup
        name="timeLimitSeconds"
        label="Time Limit (seconds)"
        description="Seconds a team gets once this challenge's timer starts. Leave empty for untimed. Only expires the challenge when the event has time limits enforced."
        error={errors.timeLimitSeconds}
      >
        <Input
          id="timeLimitSeconds"
          type="number"
          min={0}
          value={values.timeLimitSeconds ?? ''}
          onChange={e => updateField('timeLimitSeconds', parsePositiveInt(e.target.value))}
          placeholder="Untimed"
        />
      </FormFieldGroup>

      <div className="grid gap-4 sm:grid-cols-2">
        <FormFieldGroup
          name="attemptPenaltyPoints"
          label="Points Lost per Wrong Attempt"
          description="Flat deduction. Leave empty for none."
          error={errors.attemptPenaltyPoints}
        >
          <Input
            id="attemptPenaltyPoints"
            type="number"
            min={0}
            value={values.attemptPenaltyPoints ?? ''}
            onChange={e => updateField('attemptPenaltyPoints', parsePositiveInt(e.target.value))}
            placeholder="None"
            disabled={values.attemptPenaltyPercent !== null}
          />
        </FormFieldGroup>

        <FormFieldGroup
          name="attemptPenaltyPercent"
          label="% Lost per Wrong Attempt"
          description="Percentage of the challenge's points. Use one penalty or the other, not both."
          error={errors.attemptPenaltyPercent}
        >
          <Input
            id="attemptPenaltyPercent"
            type="number"
            min={0}
            max={100}
            value={values.attemptPenaltyPercent ?? ''}
            onChange={e => updateField('attemptPenaltyPercent', parsePositiveInt(e.target.value))}
            placeholder="None"
            disabled={values.attemptPenaltyPoints !== null}
          />
        </FormFieldGroup>
      </div>

      {/* Speed bonuses by finishing position */}
      <div className="space-y-2" id="field-speedBonuses">
        <p className="text-sm font-medium">Speed Bonus</p>
        <p className="text-sm text-muted-foreground">
          Bonus points awarded to teams based on their finishing position.
        </p>
        {errors.speedBonuses && <p className="text-sm text-destructive">{errors.speedBonuses}</p>}
        <div className="space-y-2">
          {values.speedBonuses.map((value, index) => (
            <div key={index} className="flex items-center gap-2">
              <div className="flex-1 space-y-1">
                <Label className="text-xs text-muted-foreground">
                  {getOrdinal(index + 1)} Place
                </Label>
                <Input
                  type="number"
                  min={0}
                  value={value || ''}
                  onChange={e => {
                    const updated = [...values.speedBonuses];
                    updated[index] = parseInt(e.target.value, 10) || 0;
                    updateField('speedBonuses', updated);
                  }}
                  placeholder="0"
                />
              </div>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="mt-5 h-9 w-9 p-0 text-muted-foreground hover:text-destructive"
                onClick={() =>
                  updateField(
                    'speedBonuses',
                    values.speedBonuses.filter((_, i) => i !== index),
                  )
                }
                aria-label={`Remove ${getOrdinal(index + 1)} place bonus`}
              >
                &times;
              </Button>
            </div>
          ))}
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => updateField('speedBonuses', [...values.speedBonuses, 0])}
          >
            Add Tier
          </Button>
        </div>
      </div>
    </SectionCard>
  );
}
